import React, { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import type { ColumnDef } from '@tanstack/react-table';
import SPADataTable from '../shared/SPADataTable';
import SPAPill from '../shared/SPAPill';
import SyncFilters from './SyncFilters';
import useSyncRows, { FILTER_KEYS, hasScope } from './useSyncRows';
import type { FilterKey, SyncFilters as Filters, SyncRow } from './useSyncRows';
import { STATE_LABELS, STATE_TONES } from './syncStates';
import AssignmentsTable from './AssignmentsTable';
import CourseMatchPanel from './CourseMatchPanel';
import useCourseMatch from './useCourseMatch';
import FilterPill from './FilterPill';
import useAssignmentRows from './useAssignmentRows';
import useRepairs from './useRepairs';
import { defaultFilter, matchesFilter } from './repairs';
import type { RepairFilter } from './repairs';
import { ranked } from './Findings';
import type { Finding } from './Findings';
import { GradeSyncLink, linkClass } from './gradeSyncPath';

const column = createColumnHelper<SyncRow>();

const formatGap = (seconds: number | null) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;

  return `${Math.round(seconds / 3600)}h`;
};

interface TableMeta { diagnostics: Record<string, Finding[]> }

// AIDEV-NOTE: Same as the assignment table's — always shown, repetition included. It is the way
// through to the teacher's page, which matters most in exactly the case where it repeats.
const teacherColumn = column.accessor('teacher', {
  cell: (info) => {
    const { teacher_id: teacherId } = info.row.original;

    if (teacherId == null) return info.getValue() ?? '—';

    return (
      <GradeSyncLink
        to={`/teachers/${teacherId}`}
      >
        {info.getValue() ?? teacherId}
      </GradeSyncLink>
    );
  },
  header: 'Teacher',
});

const columns = [
  column.accessor('student_name', {
    cell: info => (
      <div>
        <div>{info.getValue() ?? '—'}</div>
        <div className="tw-font-mono tw-text-xs tw-text-spa-tertiary">{info.row.original.student_id}</div>
      </div>
    ),
    header: 'Student',
  }),
  column.accessor('assignment_title', {
    cell: info => (
      <div>
        <Link
          className={linkClass}
          to={`../assignments/${info.row.original.assignment_id}`}
        >
          {info.getValue()}
        </Link>
        <div className="tw-font-mono tw-text-xs tw-text-spa-tertiary">{info.row.original.assignment_id}</div>
      </div>
    ),
    header: 'Assignment',
  }),
  teacherColumn,
  column.accessor('platform', { cell: info => info.getValue() ?? '—', header: 'Platform' }),
  column.accessor('grading', {
    cell: info => <SPAPill tone="neutral">{info.getValue()}</SPAPill>,
    header: 'Grading',
  }),
  column.accessor('current_score', {
    cell: (info) => {
      const { synced_score: synced } = info.row.original;

      return `${info.getValue() ?? '—'} → ${synced ?? '—'}`;
    },
    header: 'Score (TCI → LMS)',
  }),
  column.accessor('state', {
    cell: info => (
      <SPAPill tone={STATE_TONES[info.getValue()]}>{STATE_LABELS[info.getValue()]}</SPAPill>
    ),
    header: 'Current status',
  }),
  column.accessor('attempts', { header: 'Attempts (this score)', meta: { align: 'right' } }),
  column.accessor('synced_at', {
    cell: info => (info.getValue() ? new Date(info.getValue() as string).toLocaleString() : '—'),
    header: 'Last successful sync',
  }),
  column.accessor('seconds_to_sync', {
    cell: info => formatGap(info.getValue()),
    header: 'Time to sync',
    meta: { align: 'right' },
  }),
  column.display({
    cell: (info) => {
      const meta = info.table.options.meta as TableMeta | undefined;
      const found = meta?.diagnostics?.[String(info.row.original.assignment_id)] ?? [];

      if (found.length === 0) return '—';

      const [worst, ...rest] = ranked(found);

      return (
        <span title={worst.detail}>
          {worst.title}
          {rest.length > 0 && (
            <span className="tw-text-spa-tertiary">{` +${rest.length}`}</span>
          )}
        </span>
      );
    },
    header: 'Why not',
    id: 'diagnosis',
  }),
] as ColumnDef<SyncRow, unknown>[];

const readFilters = (params: URLSearchParams): Filters => (
  Object.fromEntries(FILTER_KEYS.map(key => [key, params.get(key) ?? '']).filter(([, v]) => v !== ''))
);

const TroubleshootTab = () => {
  const [params, setParams] = useSearchParams();
  const applied = useMemo(() => readFilters(params), [params]);
  const [draft, setDraft] = useState<Filters>(applied);
  const [state, setState] = useState('');
  const [platform, setPlatform] = useState('');
  // AIDEV-NOTE: Assignment grain is the default because every support case we worked resolved there —
  // the score grain answers "is this child's grade in", which is the follow-up, not the opening move.
  // Except when a student is picked: then that is the question asked, and assignment-wide counts
  // ("25 of 42") never said whether this student's grade is in.
  const [pickedGrain, setGrain] = useState<'assignments' | 'scores' | null>(null);
  const grain = pickedGrain ?? ((applied.student_id ?? '').trim() === '' ? 'assignments' : 'scores');
  // AIDEV-NOTE: Owned here because the panel and the table are two views of one answer — the panel
  // summarises it and the table shows it per row. Two components fetching it separately is what had
  // the page rendering the same assignments twice. It runs as soon as a teacher is picked, as on the
  // teacher page; the rosters are cached for ten minutes, so it was a button with nothing to decide.
  const matched = (applied.staffer_id ?? '').trim() !== '';
  // AIDEV-NOTE: A teacher and nothing narrower is exactly the teacher page, so the assignment view
  // points there rather than rendering the same table and repairs a second time. Score grain stays.
  const teacherOnly = matched && FILTER_KEYS.every(key => key === 'staffer_id' || (applied[key] ?? '').trim() === '');
  const byAssignment = grain === 'assignments' && !teacherOnly;

  const { data, error, isFetching } = useSyncRows(applied, grain === 'scores');
  const stafferId = applied.staffer_id ?? '';
  const match = useCourseMatch(stafferId, matched && byAssignment);
  const matchById = useMemo(() => (
    match.data?.rows == null ? null : new Map(match.data.rows.map(row => [row.assignment_id, row]))
  ), [match.data]);
  const assignments = useAssignmentRows(applied, byAssignment);
  const [pickedFilter, setRepairFilter] = useState<RepairFilter | null>(null);
  const { checking, repairs } = useRepairs(stafferId, assignments.data?.rows, match.data, matched && byAssignment);
  const repairFilter = pickedFilter ?? defaultFilter(repairs);
  const assignmentRows = (assignments.data?.rows ?? [])
    .filter(row => repairs == null || matchesFilter(repairFilter, repairs.get(row.assignment_id)));
  const rows = data?.rows ?? [];
  const assignmentCount = byAssignment ? (assignments.data?.rows ?? []).length : null;
  // AIDEV-NOTE: Who you have scoped to, said once. The teacher column is dropped when the whole table
  // is one teacher's — correct, it would repeat down every row — but nothing replaced it, so filtering
  // to a person left the page never naming them and no way through to their page.
  const scopedTo = [...(assignments.data?.rows ?? []), ...rows]
    .find(row => row.teacher_id != null && String(row.teacher_id) === stafferId.trim());

  const platforms = useMemo(
    () => Array.from(new Set(rows.map(r => r.platform).filter(Boolean))) as string[],
    [rows],
  );

  const visible = rows.filter(row => (
    (state === '' || row.state === state) && (platform === '' || row.platform === platform)
  ));
  const synced = visible.filter(r => r.state === 'in_sync').length;
  const percent = visible.length > 0 ? Math.round((synced / visible.length) * 100) : null;

  const pick = (key: FilterKey, value: string) => {
    const nextDraft = { ...draft, [key]: value };
    const next = new URLSearchParams();
    FILTER_KEYS.forEach((name) => {
      const picked = (nextDraft[name] ?? '').trim();
      if (picked !== '') next.set(name, picked);
    });
    setDraft(nextDraft);
    setParams(next);
  };

  const clear = () => {
    setDraft({});
    setState('');
    setPlatform('');
    setRepairFilter(null);
    setGrain(null);
    setParams(new URLSearchParams());
  };

  return (
    <>
      <SyncFilters
        draft={draft}
        grain={grain}
        matched={grain === 'assignments' ? assignmentCount : visible.length}
        platform={platform}
        platforms={platforms}
        state={state}
        total={grain === 'assignments' ? assignmentCount : rows.length}
        onClear={clear}
        onPick={pick}
        onPlatformChange={setPlatform}
        onStateChange={setState}
      />

      {error != null && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-error">
          Couldn&apos;t load those records. Check the IDs and try again.
        </p>
      )}

      {scopedTo != null && (
        <p className="tw-mb-3 tw-mt-0 tw-text-sm tw-text-spa-secondary">
          Showing
          <GradeSyncLink
            className="tw-mx-1"
            to={`/teachers/${scopedTo.teacher_id}`}
          >
            {scopedTo.teacher ?? `staffer ${scopedTo.teacher_id}`}
          </GradeSyncLink>
          — open their page for the account-level view.
        </p>
      )}

      {hasScope(applied) && (
        <div className="tw-mb-4 tw-flex tw-items-center tw-gap-2">
          <FilterPill active={grain === 'assignments'} onClick={() => setGrain('assignments')}>
            By assignment
          </FilterPill>
          <FilterPill active={grain === 'scores'} onClick={() => setGrain('scores')}>
            By score
          </FilterPill>
        </div>
      )}

      {!hasScope(applied) && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
          Enter at least one filter above. Unscoped, this would be every score on the platform —
          millions of rows with no useful answer.
        </p>
      )}

      {grain === 'assignments' && teacherOnly && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">
          {'All of this teacher\'s assignments, with what to repair, are on '}
          <GradeSyncLink to={`/teachers/${stafferId.trim()}`}>their teacher page</GradeSyncLink>
          . Pick a class, assignment or student above to narrow it down here, or switch to By score.
        </p>
      )}

      {hasScope(applied) && byAssignment && (
        <>
          <CourseMatchPanel
            assignments={assignments.data?.rows}
            checking={checking}
            filter={repairFilter}
            match={match}
            repairs={repairs}
            stafferId={stafferId}
            onFilter={setRepairFilter}
          />
          <AssignmentsTable
            isLoading={assignments.isFetching}
            match={repairs == null ? matchById : null}
            repairs={repairs}
            rows={assignmentRows}
            stafferId={stafferId}
          />
        </>
      )}

      {hasScope(applied) && grain === 'scores' && (
        <>
          {percent != null && (
            <div
              className="tw-mb-4 tw-rounded tw-border tw-border-solid tw-border-spa-border tw-bg-spa-bg-secondary
                tw-px-4 tw-py-3 tw-text-sm tw-text-spa-primary"
            >
              {`Of these ${visible.length} records, ${synced} (${percent}%) are currently synced.`}
              <span className="tw-text-spa-tertiary">
                {' '}
                Same definition as every other number here — a score counts as synced only when the
                value we last delivered still equals the current one.
              </span>
            </div>
          )}

          <SPADataTable
            columns={columns}
            data={visible}
            defaultSort={[]}
            emptyMessage={(applied.student_id ?? '').trim() === '' || rows.length > 0
              ? 'No graded work matches those filters.'
              : 'This student has no scored work here yet. By assignment lists every assignment they are on.'}
            footnote={(
              <>
                &ldquo;Not delivered&rdquo; means a score exists and nothing was ever accepted by the
                LMS. Open the assignment to see whether it is withheld on purpose, retrying, or
                genuinely stuck.
              </>
            )}
            globalFilter={{ placeholder: 'Filter these results…' }}
            isLoading={isFetching}
            meta={{ diagnostics: data?.diagnostics ?? {} }}
            pagination={{ defaultPageSize: 50 }}
          />
        </>
      )}
    </>
  );
};

export default TroubleshootTab;
